import { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { PatternFormat } from "react-number-format";
import { twMerge } from "tailwind-merge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import Editor from "components/Editor";
import ConsoleHrozontalHeadTable from "../-components/ConsolehorizontalHeadTable";
import { toastErrorSyle } from "hooks/utils/util";
import LoadingPop from "components/popup/LoadingPop";
import { usePutConsoleSupplies } from "hooks/console/usePutConsoleSupplies";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import { CommentDataProps } from "hooks/console/useGetConsoleQrSystemDetail";

const theadStyle = "text-[#222222] text-[16px] text-center";
const excludedKeys = ["notice", "b_file", "list_no", "contents"]; // 제외할 키 목록

export type SuppliesFormProps = {
    status: string;
    category: string;
    list_no: string;
    name: string;
    subject: string;
    contents: string;
    company: string;
    mobile: string;
    address: string;
    w_date: string;
    comment: CommentDataProps[];
};

export default function SuppliesForm({
    type,
    form,
    setForm,
    setEditMode,
    update,
    refetchDetail,
}: {
    type: string;
    form: SuppliesFormProps;
    setForm: React.Dispatch<React.SetStateAction<SuppliesFormProps>>;
    setEditMode?: React.Dispatch<React.SetStateAction<boolean>>;
    update?: boolean;
    refetchDetail: () => void;
}) {
    const navigate = useNavigate();
    const { mutate: updateSupplies, isPending: isPendingUpdate } = usePutConsoleSupplies();

    // console.log("form", form);

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();

        const isFormEmpty = Object.entries(form).some(([key, value]) => {
            if (excludedKeys.includes(key)) return false;

            if (typeof value === "string") {
                // contents 같은 HTML 입력값의 경우 비어 있는지 검사
                // if (key === "contents") return isEditorEmpty(value);
                return value.trim() === "";
            }

            return !value;
        });

        if (isFormEmpty) {
            let message = "입력 필드를 모두 채워해주세요.";
            if (form.subject.trim() === "") {
                message = "제목을 입력해주세요.";
            }
            toast.error(message, toastErrorSyle);
            return;
        }

        if (update && form.list_no) {
            updateSupplies(form, {
                onSuccess: (data: any) => {
                    toast.success("성공적으로 수정 되었습니다.");
                    setEditMode && setEditMode(false);
                    refetchDetail();
                },
                onError: (error: any) => {
                    const message = error.message;
                    toast.error(message, toastErrorSyle);
                },
            });
            return;
        }
        // mutate(
        //     { ...form },
        //     {
        //         onSuccess: (data: any) => {
        //             navigate(`/${type}/manage/notices/${data.data.list_no}`, { state: { page: location.state.page ?? "1" } });
        //         },
        //         onError: (error: any) => {
        //             const message = error.message;
        //             toast.error(message, toastErrorSyle);
        //         },
        //     },
        // );
    };

    return (
        <>
            {isPendingUpdate ? (
                <LoadingPop />
            ) : (
                <form onSubmit={onSubmit}>
                    <div className="mt-[8px] mb-[40px]">
                        <ConsoleHrozontalHeadTable
                            thead={[
                                {
                                    title: "처리상태",
                                    key: "status",
                                    content: (value: string) => (
                                        <Select value={value} onValueChange={value => setForm(prev => ({ ...prev, status: value }))}>
                                            <SelectTrigger
                                                className={`relative text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2  border-none shadow-none max-w-[100px] justify-center ${value === "Y" ? "text-[#4F46E5]" : "text-[#999999]"}`}
                                            >
                                                <SelectValue placeholder="" />
                                                <ArrowDown className="[&_path]:fill-[#1C1B1F]" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white">
                                                <SelectItem value="Y">처리완료</SelectItem>
                                                <SelectItem value="N">접수중</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    ),
                                },
                                {
                                    title: "제목",
                                    key: "subject",
                                    tdClassName: twMerge(theadStyle, "text-[#666666] text-left"),
                                    content: (row: string) => (
                                        <div>
                                            <input
                                                type="text"
                                                value={form.subject}
                                                onChange={e => setForm(prev => ({ ...prev, subject: e.target.value }))}
                                                className="border rounded-[5px] px-2 py-1 w-full"
                                            />
                                        </div>
                                    ),
                                },
                                {
                                    title: "회사명",
                                    key: "company",
                                    tdClassName: twMerge(theadStyle, "text-[#666666] text-left"),
                                    content: (row: string) => (
                                        <div>
                                            <input
                                                type="text"
                                                value={form.company}
                                                onChange={e => setForm(prev => ({ ...prev, company: e.target.value }))}
                                                className="border rounded-[5px] px-2 py-1 w-full"
                                            />
                                        </div>
                                    ),
                                },
                                {
                                    title: "작성자",
                                    key: "name",
                                    content: (row: string) => (
                                        <div>
                                            <input
                                                type="text"
                                                value={form.name}
                                                disabled
                                                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                                                className="border rounded-[5px] px-2 py-1 w-full"
                                            />
                                        </div>
                                    ),
                                },
                                {
                                    title: "작성일",
                                    key: "w_date",
                                    content: (row: string) => <p className="px-2">{row}</p>,
                                },
                                {
                                    title: "전화번호",
                                    key: "mobile",
                                    content: (row: string) => (
                                        <div>
                                            <PatternFormat
                                                format="###-####-####"
                                                id="mobile"
                                                name="mobile"
                                                value={form.mobile}
                                                onChange={e => setForm(prev => ({ ...prev, mobile: e.target.value }))}
                                                className="border rounded-[5px] px-2 py-1 w-full"
                                            />
                                        </div>
                                    ),
                                },
                                {
                                    title: "주소",
                                    key: "address",
                                    content: (row: string) => (
                                        <div>
                                            <input
                                                type="text"
                                                value={form.address}
                                                onChange={e => setForm(prev => ({ ...prev, address: e.target.value }))}
                                                className="border rounded-[5px] px-2 py-1 w-full"
                                            />
                                        </div>
                                    ),
                                },
                                // {
                                //     title: "상단고정",
                                //     key: "notice",
                                //     tdClassName: twMerge(theadStyle, "text-[#666666]"),
                                //     content: (row: string) => (
                                //         <div className="flex items-center gap-2">
                                //             <input
                                //                 type="checkbox"
                                //                 id="notice"
                                //                 checked={form.notice ? true : false}
                                //                 onChange={e => setForm(prev => ({ ...prev, notice: e.target.checked ? 1 : 0 }))}
                                //                 className="border"
                                //             />
                                //             <label htmlFor="notice">상단고정 등록시 체크해주세요.</label>
                                //         </div>
                                //     ),
                                // },
                            ]}
                            tbody={form}
                        />
                        <div id="bounds">
                            <Editor
                                bounds="#bounds"
                                value={form.contents}
                                onChangeHandler={(val: string) => setForm(prev => ({ ...prev, contents: val }))}
                                className="[&_.ql-editor]:min-h-[350px] h-auto responsive650:[&_.ql-editor]:min-h-[150px]"
                            />
                        </div>

                        <div className="flex justify-center gap-2 mt-5">
                            <button className="bg-black text-white rounded-[5px] px-4 py-2">저장</button>
                            <button
                                type="button"
                                className="bg-black text-white rounded-[5px] px-4 py-2"
                                onClick={() => (update && setEditMode ? setEditMode(false) : navigate(-1))}
                            >
                                {update ? "취소" : "목록"}
                            </button>
                        </div>
                    </div>
                </form>
            )}
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
